// Beginner Yoga in Frome - Gentle introduction for newcomers
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Shield, Users, Check } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../components/shared/SEOHelper";

const BeginnerYogaPage = () => {
  usePageMeta({
    title: "Beginner Yoga Frome | Gentle Yoga for Newcomers",
    description: "New to yoga? Beginner-friendly yoga classes in Frome, Somerset. Small groups, patient teaching, no flexibility required.",
    canonical: "https://annelielily.com/yoga-classes/beginner-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Beginner Yoga Classes in Frome",
    description: "Gentle, beginner-friendly yoga classes in Frome, Somerset. Small groups with patient teaching for those new to yoga.",
    serviceType: "Yoga Class",
    url: "https://annelielily.com/yoga-classes/beginner-yoga"
  });

  const faqs = [
    {
      question: "I'm not flexible at all. Can I still do yoga?",
      answer: "Absolutely. Yoga isn't about being flexible — it's about becoming more comfortable in your body, whatever that looks like for you. I've taught many people who couldn't touch their toes when they started. Some still can't, and that's perfectly fine."
    },
    {
      question: "Will I be the only beginner in class?",
      answer: "No. My classes always have a mix of experience levels, and I teach in a way that works for everyone. You won't be singled out or made to feel like you're behind."
    },
    {
      question: "What if I can't do a pose?",
      answer: "Then we'll find something else that works for your body. I offer modifications throughout every class, and you're always welcome to rest. There's no such thing as 'failing' at yoga."
    },
    {
      question: "Should I start with a private session or group class?",
      answer: "Either works. Group classes are fine for beginners — I'll look after you. But if you're nervous about joining a group, or have specific concerns (injuries, health conditions), a private session first can help build confidence."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Beginner Yoga" currentSlug="yoga-classes/beginner-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              New to Yoga?
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Beginner Yoga in Frome
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              If you've been thinking about trying yoga but aren't sure where to start, 
              or if the idea of walking into a yoga class feels a bit daunting, you're 
              in the right place. Everyone starts somewhere.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Book Your First Class <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/yoga-classes" className="btn-secondary">
                View All Classes
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Reassurance */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What to Expect
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                <strong>You don't need to be flexible.</strong> Yoga isn't about being able 
                to touch your toes. It's about becoming more comfortable in your body, 
                whatever that looks like for you right now.
              </p>
              <p>
                <strong>You don't need special equipment.</strong> I provide mats, blocks, 
                and blankets. Just wear comfortable clothes you can move in.
              </p>
              <p>
                <strong>You're welcome as you are.</strong> My classes are small and supportive. 
                Everyone is focused on their own practice, and there's no competition or comparison.
              </p>
              <p>
                <strong>You can rest whenever you need to.</strong> Child's pose is always 
                available. If something doesn't feel right, you simply rest. That's not 
                failure — that's listening to your body.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Makes It Beginner-Friendly */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              A Welcoming Environment for Beginners
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: "Small Groups",
                desc: "Classes are kept small so there's space for individual guidance and attention."
              },
              {
                icon: Heart,
                title: "Patient Teaching",
                desc: "I explain things clearly, demonstrate each pose, and give you time to find your way."
              },
              {
                icon: Shield,
                title: "Modifications Available",
                desc: "Every pose has gentler variations. I'll show you options that work for your body."
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/70 p-6 rounded-xl border border-gold/10 text-center"
              >
                <item.icon className="w-8 h-8 text-gold mx-auto mb-4" />
                <h3 className="font-heading text-xl text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Which Class to Start With */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Where to Start
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                <strong>If you're relatively active</strong> and want a well-rounded practice, 
                start with <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link>. 
                It's the classic style — postures, breathing, relaxation — and suits most people.
              </p>
              <p>
                <strong>If you're stressed, exhausted, or very tight</strong>, 
                <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> might 
                be a gentler entry point. We hold poses for longer, let gravity do the work, and 
                focus on release rather than strength.
              </p>
              <p>
                <strong>If you prefer one-to-one attention</strong>, consider a 
                <Link to="/yoga-classes/private-yoga" className="text-gold hover:underline">private session</Link> first. 
                We can work at your pace, address any concerns, and build your confidence before 
                joining a group.
              </p>
              <p>
                <strong>Not sure?</strong> <Link to="/contact" className="text-gold hover:underline">Get in touch</Link> and tell me a bit about yourself. 
                I'll suggest the best starting point.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Questions Beginners Often Ask
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Give It a Try?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Your first class is the hardest part — after that, it gets easier. 
              Get in touch and I'll help you take that first step.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Book Your First Class <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <a href="tel:07540995021" className="btn-secondary">
                Call 07540 995021
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default BeginnerYogaPage;
